import { vehiclesApiTemplateSlice as api } from '../state/vehicles/vehiclesApiTemplateSlice';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getVehiclesV2: build.query<GetVehiclesV2ApiResponse, GetVehiclesV2ApiArg>({
      query: () => ({ url: `/v2/vehicles` }),
    }),
    getVehicleByIdV2: build.query<
      GetVehicleByIdV2ApiResponse,
      GetVehicleByIdV2ApiArg
    >({
      query: (queryArg) => ({ url: `/v2/vehicles/${queryArg.vehicleId}` }),
    }),
    updateServiceActivationsV2: build.mutation<
      UpdateServiceActivationsV2ApiResponse,
      UpdateServiceActivationsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/vehicles/${queryArg.vehicleId}`,
        method: 'PATCH',
        body: queryArg.updateServiceActivationsRequest,
      }),
    }),
    addPaymentMethodV2: build.mutation<
      AddPaymentMethodV2ApiResponse,
      AddPaymentMethodV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/vehicles/${queryArg.vehicleId}/paymentMethods`,
        method: 'POST',
        body: queryArg.addPaymentMethodRequest,
      }),
    }),
    removePaymentMethodV2: build.mutation<
      RemovePaymentMethodV2ApiResponse,
      RemovePaymentMethodV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/vehicles/${queryArg.vehicleId}/paymentMethods/${queryArg.paymentMethodId}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetVehiclesV2ApiResponse = /** status 200 OK */ VehiclesList;
export type GetVehiclesV2ApiArg = void;
export type GetVehicleByIdV2ApiResponse = /** status 200 OK */ Vehicle;
export type GetVehicleByIdV2ApiArg = {
  /** Vehicle ID in context of RIO asset management */
  vehicleId: string;
};
export type UpdateServiceActivationsV2ApiResponse =
  /** status 200 OK */ Vehicle;
export type UpdateServiceActivationsV2ApiArg = {
  /** Vehicle ID in context of RIO asset management */
  vehicleId: string;
  updateServiceActivationsRequest: UpdateServiceActivationsRequest;
};
export type AddPaymentMethodV2ApiResponse =
  /** status 201 CREATED */ PaymentMethod;
export type AddPaymentMethodV2ApiArg = {
  /** Vehicle ID in context of RIO asset management */
  vehicleId: string;
  addPaymentMethodRequest: AddPaymentMethodRequest;
};
export type RemovePaymentMethodV2ApiResponse =
  /** status 204 NO CONTENT */ void;
export type RemovePaymentMethodV2ApiArg = {
  /** Vehicle ID in context of RIO asset management */
  vehicleId: string;
  /** Payment method ID in context of a vehicle */
  paymentMethodId: string;
};
export type PaymentMethodExpiryDate = string;
export type PaymentMethod = {
  id: string;
  type: PaymentMethodType;
  state: PaymentMethodState;
  paymentPartner: PaymentPartnerType;
  lastFourDigits: string;
  expiry: PaymentMethodExpiryDate;
  referenceId: string;
};
export type PaymentMethodAssignment = {
  /** Reference to `vehicle.paymentMethods[].id`. */
  paymentMethodId: string;
};
export type ServiceActivation = {
  serviceType: ServiceType;
  paymentMethodAssignments: PaymentMethodAssignment[];
};
export type Vehicle = {
  id: string;
  name: string;
  vin: string;
  licensePlate?: string;
  type: VehicleType;
  /** Indicates that the vehicle was not activated for SimplePay yet. */
  enabled: boolean;
  group_ids: string[];
  brand: string;
  paymentMethods: PaymentMethod[];
  serviceActivations: ServiceActivation[];
};
export type VehiclesList = {
  items: Vehicle[];
};
export type ErrorResponse = {
  message: string;
  errorCode: ErrorCode;
  fields: string[];
  /** Name of the vehicle the card is already linked to. Only present for CARD_ALREADY_IN_USE_SAME_ACCOUNT. */
  vehicleName?: string;
};
export type ServiceActivationUpdate = {
  serviceType: ServiceType;
  paymentMethodAssignments: PaymentMethodAssignment[];
  enabled: boolean;
};
export type UpdateServiceActivationsRequest = {
  serviceActivations: ServiceActivationUpdate[];
};
export type UtaOnboardingDetails = {
  /** Token representing the user at UTA. */
  userToken: string;
};
export type AddPaymentMethodRequest = {
  /** ID of the payment method at the payment partner's system. Can be a card number or another identifier,
    depending on the partner.
     */
  referenceId: string;
  type: PaymentMethodType;
  paymentPartner: PaymentPartnerType;
  /** Service Card Number */
  cardNumber: string;
  expiry?: PaymentMethodExpiryDate;
  onboardingDetails?: UtaOnboardingDetails;
};
export enum VehicleType {
  Truck = 'TRUCK',
  Bus = 'BUS',
  Van = 'VAN',
  Trailer = 'TRAILER',
  Car = 'CAR',
}
export enum PaymentMethodType {
  FuelCard = 'FUEL_CARD',
  ChargeCard = 'CHARGE_CARD',
}
export enum PaymentMethodState {
  Active = 'ACTIVE',
  OnboardingPending = 'ONBOARDING_PENDING',
  OnboardingFailure = 'ONBOARDING_FAILURE',
}
export enum PaymentPartnerType {
  MockFuelingPayment = 'MOCK_FUELING_PAYMENT',
  UtaPayment = 'UTA_PAYMENT',
  DkvPayment = 'DKV_PAYMENT',
  ManChargeAndGoPayment = 'MAN_CHARGE_AND_GO_PAYMENT',
}
export enum ServiceType {
  Fueling = 'FUELING',
  Breakdown = 'BREAKDOWN',
  Parking = 'PARKING',
  Washing = 'WASHING',
  Charging = 'CHARGING',
  Other = 'OTHER',
}
export enum ErrorCode {
  InvalidPathParameters = 'INVALID_PATH_PARAMETERS',
  InvalidQueryParameters = 'INVALID_QUERY_PARAMETERS',
  InvalidPayload = 'INVALID_PAYLOAD',
  InvalidFuelCard = 'INVALID_FUEL_CARD',
  InvalidStatusChange = 'INVALID_STATUS_CHANGE',
  TransactionStateNotCancelable = 'TRANSACTION_STATE_NOT_CANCELABLE',
  PoiNotFound = 'POI_NOT_FOUND',
  OnlinePaymentNotSupported = 'ONLINE_PAYMENT_NOT_SUPPORTED',
  PaymentMethodNotDeletable = 'PAYMENT_METHOD_NOT_DELETABLE',
  PaymentMethodNotAssignable = 'PAYMENT_METHOD_NOT_ASSIGNABLE',
  VehicleNotFound = 'VEHICLE_NOT_FOUND',
  VehicleServiceNotActivated = 'VEHICLE_SERVICE_NOT_ACTIVATED',
  VehicleDoesNotHaveLicensePlate = 'VEHICLE_DOES_NOT_HAVE_LICENSE_PLATE',
  PaymentMethodNotFound = 'PAYMENT_METHOD_NOT_FOUND',
  PaymentMethodNotUsableAtLocation = 'PAYMENT_METHOD_NOT_USABLE_AT_LOCATION',
  BookingNotCancelable = 'BOOKING_NOT_CANCELABLE',
  CardAlreadyInUseSameAccount = 'CARD_ALREADY_IN_USE_SAME_ACCOUNT',
  CardAlreadyInUseOtherAccount = 'CARD_ALREADY_IN_USE_OTHER_ACCOUNT',
  UnknownError = 'UNKNOWN_ERROR',
}
export const {
  useGetVehiclesV2Query,
  useGetVehicleByIdV2Query,
  useUpdateServiceActivationsV2Mutation,
  useAddPaymentMethodV2Mutation,
  useRemovePaymentMethodV2Mutation,
} = injectedRtkApi;
